import { useState } from "react";
import { Lock } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { api, money } from "../lib/api";
import Seo from "../components/Seo";

const FIELDS = [
  { name: "name", label: "Full name", testid: "checkout-name-input", required: true },
  { name: "email", label: "Email", type: "email", testid: "checkout-email-input", required: true },
  { name: "line1", label: "Address line 1", testid: "checkout-address1-input", required: true },
  { name: "line2", label: "Address line 2 (optional)", testid: "checkout-address2-input" },
  { name: "city", label: "City", testid: "checkout-city-input", required: true },
  { name: "state", label: "State", testid: "checkout-state-input" },
  { name: "zip", label: "ZIP", testid: "checkout-zip-input", required: true },
];

export default function CheckoutPage() {
  const { cart, cartId } = useStore();
  const [form, setForm] = useState({ line1: "", line2: "", city: "", state: "", zip: "", name: "", email: "", gift_note: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api("/checkout", {
        method: "POST",
        body: {
          cart_id: cartId,
          name: form.name,
          email: form.email,
          address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, zip: form.zip, country: "US" },
          gift_note: form.gift_note,
          origin_url: window.location.origin,
        },
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if ((cart.items || []).length === 0) {
    return (
      <div className="bg-candy-mist min-h-screen text-center py-24">
        <h1 className="font-display font-semibold text-3xl">Your bag is empty</h1>
        <p className="text-muted mt-2 font-semibold">Add something cozy before checking out.</p>
        <a href="/shop" data-testid="checkout-empty-shop-link" className="mt-6 inline-block px-6 py-3 rounded-full bg-candy text-white font-bold shadow-pink">Browse creations</a>
      </div>
    );
  }

  return (
    <div className="bg-candy-mist min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <Seo title="Checkout" path="/checkout" noindex />
        <h1 data-testid="checkout-heading" className="font-display font-semibold text-3xl sm:text-4xl text-center">Checkout</h1>
        <p className="text-center text-muted font-semibold mt-2 flex items-center justify-center gap-1.5"><Lock className="w-4 h-4 text-mint-accent" /> Redirects to Stripe's secure payment page — test mode</p>
        <form onSubmit={submit} data-testid="checkout-form" className="mt-10 grid md:grid-cols-5 gap-8 items-start">
          <div className="md:col-span-3 bg-white rounded-3xl border border-candy-soft/70 p-6 space-y-4">
            {FIELDS.map((f) => (
              <label key={f.name} className="block">
                <span className="text-sm font-bold text-ink/80">{f.label}</span>
                <input
                  data-testid={f.testid}
                  type={f.type || "text"}
                  required={f.required}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold"
                />
              </label>
            ))}
            <label className="block">
              <span className="text-sm font-bold text-ink/80">Gift note (optional)</span>
              <textarea data-testid="checkout-gift-note-input" rows={2} maxLength={500} value={form.gift_note} onChange={(e) => setForm({ ...form, gift_note: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold resize-none" />
            </label>
            {error && <p className="text-red-500 text-sm font-bold" data-testid="checkout-error">{error}</p>}
            <button disabled={busy} data-testid="checkout-place-order-button" className="w-full py-4 rounded-full bg-candy hover:bg-candy-deep disabled:opacity-60 text-white font-display font-semibold text-lg shadow-pink transition-colors duration-200">
              {busy ? "Opening Stripe…" : `Pay ${money(cart.total_cents)} with Stripe`}
            </button>
          </div>
          <aside className="md:col-span-2 bg-white rounded-3xl border border-candy-soft/70 p-6 md:sticky md:top-28" data-testid="checkout-summary">
            <h2 className="font-display font-semibold text-xl mb-4">Order summary</h2>
            <div className="space-y-3">
              {cart.items.map((it) => (
                <div key={`${it.product_id}-${it.variant_label}`} className="flex gap-3 items-center">
                  <img src={it.image} alt="" className="w-12 h-12 rounded-xl object-cover bg-candy-soft" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{it.title} × {it.qty}</p>
                    {it.variant_label && <p className="text-xs text-muted font-semibold">{it.variant_label}</p>}
                  </div>
                  <span className="font-bold text-sm">{money(it.price_cents * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-candy-soft mt-4 pt-4 space-y-2 text-sm font-bold text-ink/70">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="checkout-subtotal">{money(cart.subtotal_cents)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span data-testid="checkout-shipping">{cart.shipping_cents === 0 ? "Free!" : money(cart.shipping_cents)}</span></div>
              <div className="flex justify-between font-display font-semibold text-lg text-ink border-t border-candy-soft pt-2"><span>Total</span><span data-testid="checkout-total">{money(cart.total_cents)}</span></div>
              <p className="text-[11px] text-muted font-semibold">Tax, if any, is calculated by Stripe at checkout.</p>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}
